import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Turnstile as TurnstileWidget, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Check, Loader2, RotateCcw } from 'lucide-react';

// Cloudflare Turnstile in its invisible mode: nothing to click, it checks the
// browser in the background and hands back a token. What the person sees is
// one status line (checking, verified, or a retry), never an empty box.

type Status = 'checking' | 'verified' | 'failed' | 'expired';

export interface HumanCheckRef {
  /** Starts a fresh check (after a failed submit, the token is spent). */
  reset: () => void;
}

interface Props {
  onToken: (token: string | null) => void;
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

/** True when forms must wait for a token: their action buttons stay disabled until the check passes. */
export const needsHumanCheck = !!SITE_KEY;

const HumanCheck = forwardRef<HumanCheckRef, Props>(({ onToken }, ref) => {
  const widget = useRef<TurnstileInstance | null>(null);
  const [status, setStatus] = useState<Status>('checking');

  const restart = () => {
    onToken(null);
    setStatus('checking');
    widget.current?.reset();
  };
  useImperativeHandle(ref, () => ({ reset: restart }));

  // Local development without a key: the gateway skips the check too.
  if (!SITE_KEY) return null;

  return (
    <div className="min-h-5 text-sm" aria-live="polite">
      <TurnstileWidget
        ref={widget}
        siteKey={SITE_KEY}
        options={{ theme: 'auto', size: 'invisible' }}
        onSuccess={(token) => { onToken(token); setStatus('verified'); }}
        onError={() => { onToken(null); setStatus('failed'); }}
        onExpire={() => { onToken(null); setStatus('expired'); }}
      />
      {status === 'checking' && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          Checking your browser…
        </p>
      )}
      {status === 'verified' && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Check className="h-4 w-4 text-success" aria-hidden="true" />
          Browser check passed
        </p>
      )}
      {(status === 'failed' || status === 'expired') && (
        <p className="flex flex-wrap items-center gap-x-2 text-error">
          {status === 'failed' ? "We couldn't check your browser." : 'The browser check expired.'}
          <button type="button" onClick={restart} className="inline-flex items-center gap-1 font-medium text-accent-text hover:underline">
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Try again
          </button>
        </p>
      )}
    </div>
  );
});

HumanCheck.displayName = 'HumanCheck';
export default HumanCheck;
